import React from "react";
import ReactFlow, {
	Node,
	Edge,
	Background,
	Controls,
	NodeTypes,
	MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import CustomNode from "./CustomNode";

const nodeTypes: NodeTypes = {
	custom: CustomNode,
};

interface SystemArchitectureProps {
	onClick: (nodeId: string, nodeData: any) => void;
}

// Define layer positions - revised for 4 clear sections
const LAYER_SPACING = 180;
const KPI_LAYER_Y = 80;
const SYSTEM_LAYER_Y = KPI_LAYER_Y + LAYER_SPACING;
const COLLECTION_LAYER_Y = SYSTEM_LAYER_Y + LAYER_SPACING;
const DEPARTMENT_LAYER_Y = COLLECTION_LAYER_Y + LAYER_SPACING;

const SystemArchitecture: React.FC<SystemArchitectureProps> = ({ onClick }) => {
	// Create nodes with onClick function passed to data
	const initialNodes: Node[] = [
		// Section labels
		{
			id: "kpi-label-left",
			type: "custom",
			position: { x: 0, y: KPI_LAYER_Y },
			data: {
				label: "KPIs",
				background: "#e0e0e0",
				orientation: "vertical",
				isLabel: true,
			},
		},
		{
			id: "systems-label-left",
			type: "custom",
			position: { x: 0, y: SYSTEM_LAYER_Y },
			data: {
				label: "Data Systems",
				background: "#e0e0e0",
				orientation: "vertical",
				isLabel: true,
			},
		},
		{
			id: "collections-label-left",
			type: "custom",
			position: { x: 0, y: COLLECTION_LAYER_Y },
			data: {
				label: "Data Collections",
				background: "#e0e0e0",
				orientation: "vertical",
				isLabel: true,
			},
		},
		{
			id: "departments-label-left",
			type: "custom",
			position: { x: 0, y: DEPARTMENT_LAYER_Y },
			data: {
				label: "Departments",
				background: "#e0e0e0",
				orientation: "vertical",
				isLabel: true,
			},
		},

		// KPI Layer
		{
			id: "national-budget",
			type: "custom",
			position: { x: 250, y: KPI_LAYER_Y },
			data: {
				label: "National Budget",
				subtitle: "Disbursements",
				background: "#ffab91",
				onClick: onClick,
			},
		},
		{
			id: "cost-coverage",
			type: "custom",
			position: { x: 500, y: KPI_LAYER_Y },
			data: {
				label: "Cost Coverage",
				subtitle: "by Revenue",
				background: "#ffab91",
				onClick: onClick,
			},
		},
		{
			id: "utility-budget",
			type: "custom",
			position: { x: 750, y: KPI_LAYER_Y },
			data: {
				label: "Utility Budget",
				subtitle: "Variance",
				background: "#ffab91",
				onClick: onClick,
			},
		},
		{
			id: "customer-satisfaction",
			type: "custom",
			position: { x: 1000, y: KPI_LAYER_Y },
			data: {
				label: "Customer Satisfaction",
				subtitle: "Feedback Score",
				background: "#ffab91",
				onClick: onClick,
			},
		},

		// Data Systems Layer
		{
			id: "sage",
			type: "custom",
			position: { x: 250, y: SYSTEM_LAYER_Y },
			data: {
				label: "SAGE Evolution",
				subtitle: "Fixed Assets",
				background: "#ffcc80",
				onClick: onClick,
			},
		},
		{
			id: "pastel",
			type: "custom",
			position: { x: 500, y: SYSTEM_LAYER_Y },
			data: {
				label: "Pastel",
				subtitle: "Financial Management",
				background: "#90caf9",
				onClick: onClick,
			},
		},
		{
			id: "promun",
			type: "custom",
			position: { x: 750, y: SYSTEM_LAYER_Y },
			data: {
				label: "PROMUN",
				subtitle: "Billing",
				background: "#a5d6a7",
				onClick: onClick,
			},
		},
		{
			id: "inventory-system",
			type: "custom",
			position: { x: 1000, y: SYSTEM_LAYER_Y },
			data: {
				label: "Inventory System",
				subtitle: "Stock Tracking",
				background: "#c5e1a5",
				onClick: onClick,
			},
		},
		{
			id: "crm",
			type: "custom",
			position: { x: 1250, y: SYSTEM_LAYER_Y },
			data: {
				label: "CRM",
				subtitle: "Customer Management",
				background: "#ce93d8",
				onClick: onClick,
			},
		},

		// Data Collections Layer
		{
			id: "payroll",
			type: "custom",
			position: { x: 250, y: COLLECTION_LAYER_Y },
			data: {
				label: "Payroll Data",
				subtitle: "Employee Records",
				background: "#fff59d",
				onClick: onClick,
			},
		},
		{
			id: "finance",
			type: "custom",
			position: { x: 500, y: COLLECTION_LAYER_Y },
			data: {
				label: "Financial Records",
				subtitle: "Accounting Data",
				background: "#ffcc80",
				onClick: onClick,
			},
		},
		{
			id: "commercial",
			type: "custom",
			position: { x: 750, y: COLLECTION_LAYER_Y },
			data: {
				label: "Commercial Data",
				subtitle: "Sales Records",
				background: "#a5d6a7",
				onClick: onClick,
			},
		},
		{
			id: "technical",
			type: "custom",
			position: { x: 1000, y: COLLECTION_LAYER_Y },
			data: {
				label: "Technical Data",
				subtitle: "Resource Tracking",
				background: "#90caf9",
				onClick: onClick,
			},
		},
		{
			id: "customer",
			type: "custom",
			position: { x: 1250, y: COLLECTION_LAYER_Y },
			data: {
				label: "Customer Data",
				subtitle: "Profiles & Interactions",
				background: "#b39ddb",
				onClick: onClick,
			},
		},

		// Department Layer (New Section)
		{
			id: "finance-dept",
			type: "custom",
			position: { x: 250, y: DEPARTMENT_LAYER_Y },
			data: {
				label: "Finance",
				subtitle: "Accounting & Treasury",
				background: "#ffcc80",
				onClick: onClick,
			},
		},
		{
			id: "it-dept",
			type: "custom",
			position: { x: 500, y: DEPARTMENT_LAYER_Y },
			data: {
				label: "IT",
				subtitle: "Systems & Support",
				background: "#90caf9",
				onClick: onClick,
			},
		},
		{
			id: "operations-dept",
			type: "custom",
			position: { x: 750, y: DEPARTMENT_LAYER_Y },
			data: {
				label: "Operations",
				subtitle: "Service Delivery",
				background: "#a5d6a7",
				onClick: onClick,
			},
		},
		{
			id: "commercial-dept",
			type: "custom",
			position: { x: 1000, y: DEPARTMENT_LAYER_Y },
			data: {
				label: "Commercial",
				subtitle: "Sales & Customer Service",
				background: "#c5e1a5",
				onClick: onClick,
			},
		},
		{
			id: "hr-dept",
			type: "custom",
			position: { x: 1250, y: DEPARTMENT_LAYER_Y },
			data: {
				label: "HR",
				subtitle: "Personnel Management",
				background: "#ce93d8",
				onClick: onClick,
			},
		},
	];

	const initialEdges: Edge[] = [
		// Departments to Data Collections
		{
			id: "e14",
			source: "finance-dept",
			target: "finance",
			type: "smoothstep",
			animated: true,
			style: { stroke: "#ffcc80" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
		{
			id: "e15",
			source: "operations-dept",
			target: "technical",
			type: "smoothstep",
			animated: true,
			style: { stroke: "#a5d6a7" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
		{
			id: "e16",
			source: "commercial-dept",
			target: "commercial",
			type: "smoothstep",
			animated: true,
			style: { stroke: "#c5e1a5" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
		{
			id: "e17",
			source: "hr-dept",
			target: "payroll",
			type: "smoothstep",
			animated: true,
			style: { stroke: "#ce93d8" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
		{
			id: "e18",
			source: "commercial-dept",
			target: "customer",
			type: "smoothstep",
			animated: true,
			style: { stroke: "#c5e1a5" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
		{
			id: "e19",
			source: "it-dept",
			target: "technical",
			type: "smoothstep",
			animated: true,
			style: { stroke: "#90caf9" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},

		// Data Collections to Data Systems
		{
			id: "e1",
			source: "payroll",
			target: "pastel",
			type: "smoothstep",
			animated: true,
			style: { stroke: "#fff59d" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
		{
			id: "e2",
			source: "finance",
			target: "pastel",
			type: "smoothstep",
			animated: true,
			style: { stroke: "#ffcc80" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
		{
			id: "e3",
			source: "commercial",
			target: "promun",
			type: "smoothstep",
			animated: true,
			style: { stroke: "#a5d6a7" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
		{
			id: "e4",
			source: "technical",
			target: "inventory-system",
			type: "smoothstep",
			animated: true,
			style: { stroke: "#90caf9" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
		{
			id: "e5",
			source: "customer",
			target: "crm",
			type: "smoothstep",
			animated: true,
			style: { stroke: "#b39ddb" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},

		// System interconnections
		{
			id: "e6",
			source: "pastel",
			target: "sage",
			type: "smoothstep",
			style: { stroke: "#666" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
		{
			id: "e7",
			source: "promun",
			target: "pastel",
			type: "smoothstep",
			style: { stroke: "#666" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
		{
			id: "e8",
			source: "inventory-system",
			target: "pastel",
			type: "smoothstep",
			style: { stroke: "#666" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
		{
			id: "e9",
			source: "crm",
			target: "pastel",
			type: "smoothstep",
			style: { stroke: "#666" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},

		// Data Systems to KPIs
		{
			id: "e10",
			source: "sage",
			target: "national-budget",
			type: "smoothstep",
			style: { stroke: "#ffab91" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
		{
			id: "e11",
			source: "sage",
			target: "cost-coverage",
			type: "smoothstep",
			style: { stroke: "#ffab91" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
		{
			id: "e12",
			source: "promun",
			target: "utility-budget",
			type: "smoothstep",
			style: { stroke: "#ffab91" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
		{
			id: "e13",
			source: "crm",
			target: "customer-satisfaction",
			type: "smoothstep",
			style: { stroke: "#ffab91" },
			markerEnd: { type: MarkerType.ArrowClosed },
		},
	];

	return (
		<div
			className="w-full py-6 mt-12 bg-[#EAF5FF]"
			style={{ width: "", height: "800px", border: "1px solid #ddd" }}>
			<ReactFlow
				nodes={initialNodes}
				edges={initialEdges}
				nodeTypes={nodeTypes}
				fitView
				zoomOnScroll={false}
				defaultEdgeOptions={{
					type: "smoothstep",
					animated: false,
				}}>
				<Background />
				<Controls />
			</ReactFlow>
		</div>
	);
};

export default SystemArchitecture;
